import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import ProductCard from '../components/product/ProductCard';
import CategoryCard from '../components/product/CategoryCard';
import StoreCard from '../components/store/StoreCard';
import SellerDashboard from './SellerDashboard';
import RecentlyViewed from '../components/common/RecentlyViewed';
import FoundingSellersCampaign from '../components/common/FoundingSellersCampaign';
import OnboardingWalkthrough from '../components/OnboardingWalkthrough';
import SEO from '../components/common/SEO';
import { FoundingTenTicker } from '../components/common/FoundingTenTicker';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { ChevronRight, Search, ShoppingBag, Heart, RefreshCw, Store, Briefcase, ArrowRight, MapPin, KeyRound } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HomePage = () => {
  const navigate = useNavigate();
  const { products, categories, user, isAuthenticated, switchRole, loading } = useApp();
  const [activeTab, setActiveTab] = useState('all');
  const [stores, setStores] = useState([]);
  const [totalStores, setTotalStores] = useState(0);
  const [buyerState, setBuyerState] = useState('');

  useEffect(() => {
    fetch(`${API_URL}/api/currency/detect`)
      .then(r => r.json())
      .then(d => {
        if (d.country === 'AU' && ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'].includes((d.region || '').toUpperCase())) {
          setBuyerState(d.region.toUpperCase());
        }
      })
      .catch(() => {});
  }, []);

  const localProducts = buyerState
    ? products.filter(p =>
        !p.sold && !p.is_example && !p.is_adult &&
        (p.pickup_available || p.pickupAvailable) &&
        ((p.state || '').toUpperCase() === buyerState || (p.location || '').toUpperCase().includes(buyerState))
      )
    : [];
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [totalItems, setTotalItems] = useState(0);

  // Show onboarding for new users
  useEffect(() => {
    const seen = localStorage.getItem('minted_onboarding_complete');
    if (!seen) setShowOnboarding(true);
  }, []);

  useEffect(() => {
    const fetchStores = async () => {
      try {
        const response = await fetch(`${API_URL}/api/stores`);
        if (response.ok) {
          const data = await response.json();
          setTotalStores(data.length);
          setStores(data.filter(s => s.product_count > 0));
        }
      } catch (error) {
        console.error('Error fetching stores:', error);
      }
    };
    fetchStores();
  }, []);

  // Fetch accurate total items count from stats endpoint
  useEffect(() => {
    const fetchItemCount = async () => {
      try {
        const res = await fetch(`${API_URL}/api/founding-sellers/stats`);
        if (res.ok) {
          const data = await res.json();
          setTotalItems(data.listings || products.length);
        }
      } catch {
        setTotalItems(products.length);
      }
    };
    fetchItemCount();
  }, [products.length]);

  if (isAuthenticated && user?.role === 'seller') {
    return <SellerDashboard />;
  }

  // "Just Listed": newest items globally, with at most 3 boosted items pinned first
  const unsoldProducts = products.filter(p => !p.sold);
  const boostedFirst = unsoldProducts.filter(p => p.is_boosted === true).slice(0, 3);
  const boostedIds = new Set(boostedFirst.map(p => p.id));
  const featuredProducts = [...boostedFirst, ...unsoldProducts.filter(p => !boostedIds.has(p.id))].slice(0, 6);

  const serviceCategories = [
    { name: 'Auto & Mechanics', icon: 'https://static.prod-images.emergentagent.com/jobs/1f1c9e33-12c0-4253-8c78-d32e923a5333/images/1861991255b9546d2c33fa7de32a136c5a7e3491c1bc926ac4519e4a89353e45.png' },
    { name: 'Hair & Beauty', icon: 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=200&h=200&fit=crop' },
    { name: 'Wellness & Massage', icon: 'https://images.unsplash.com/photo-1600334129128-685c5582fd35?w=200&h=200&fit=crop' },
    { name: 'Home & Trade', icon: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=200&h=200&fit=crop' },
  ];

  return (
    <Layout>
      <SEO 
        title="Buy & Sell Preloved Items"
        description="Your marketplace for buying and selling preloved items. Find great deals on clothing, electronics, toys, furniture & more near you."
        url="/"
      />

      {/* Onboarding */}
      {showOnboarding && (
        <OnboardingWalkthrough onComplete={() => setShowOnboarding(false)} />
      )}

      {/* Founding Ten race ticker */}
      <FoundingTenTicker />

      {/* Hero Section — Deep Forest Green */}
      <div className="relative bg-minted-900 text-white px-6 py-8 overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: `url("https://static.prod-images.emergentagent.com/jobs/1f1c9e33-12c0-4253-8c78-d32e923a5333/images/c5c1f8e94ae4487fe0163fcdc5f4046e6604276546840c551185ef6b94a9e2f5.png")`, backgroundSize: 'cover', backgroundPosition: 'center'}} />
        <div className="relative z-10">
          <p className="text-[10px] tracking-[0.25em] uppercase text-minted-300 mb-2">
            {isAuthenticated ? `Welcome back, ${user?.name?.split(' ')[0]}` : 'The Marketplace'}
          </p>
          <h1 className="font-display text-2xl sm:text-3xl font-medium leading-tight tracking-tight mb-1" data-testid="hero-heading">
            Curated Preloved,<br />Delivered to You.
          </h1>
          <p className="text-minted-300 text-sm mt-2 leading-relaxed max-w-xs">
            Buy, sell & discover premium preloved goods and local services.
          </p>

          {/* Search Bar */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const q = e.target.elements.homeSearch.value.trim();
              if (q) navigate(`/browse?q=${encodeURIComponent(q)}`);
              else navigate('/browse');
            }}
            className="mt-5 flex items-center bg-white/10 backdrop-blur-sm border border-white/20 rounded-sm overflow-hidden"
          >
            <button type="submit" className="pl-4">
              <Search className="w-4 h-4 text-minted-300" strokeWidth={1.5} />
            </button>
            <input
              name="homeSearch"
              type="text"
              placeholder="Search items & services..."
              className="flex-1 bg-transparent text-white placeholder-minted-400 text-sm py-3 px-3 outline-none"
              data-testid="home-search-input"
            />
          </form>

          {isAuthenticated && (
            <button
              onClick={switchRole}
              className="mt-3 flex items-center gap-2 text-minted-300 text-xs hover:text-white transition-colors tracking-wider uppercase"
            >
              <RefreshCw className="w-3 h-3" strokeWidth={1.5} />
              Switch to Seller Mode
            </button>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="flex border-b border-stone-200 bg-white">
        <div className="flex-1 p-3 text-center border-r border-stone-100">
          {loading && products.length === 0 ? (
            <div className="h-7 w-8 mx-auto bg-stone-100 rounded-sm animate-pulse mb-1" />
          ) : (
            <p className="text-xl font-display font-medium text-stone-900" data-testid="items-available-count">{totalItems || products.length}</p>
          )}
          <p className="text-[10px] text-stone-400 tracking-wider uppercase">Items</p>
        </div>
        <div className="flex-1 p-3 text-center border-r border-stone-100">
          {loading && categories.length === 0 ? (
            <div className="h-7 w-8 mx-auto bg-stone-100 rounded-sm animate-pulse mb-1" />
          ) : (
            <p className="text-xl font-display font-medium text-stone-900" data-testid="categories-count">{categories.length}</p>
          )}
          <p className="text-[10px] text-stone-400 tracking-wider uppercase">Categories</p>
        </div>
        <div className="flex-1 p-3 text-center">
          <p className="text-xl font-display font-medium text-minted-700">Shippit</p>
          <p className="text-[10px] text-stone-400 tracking-wider uppercase">Shipping</p>
        </div>
      </div>

      {/* Founding Sellers Campaign */}
      <FoundingSellersCampaign />

      {/* Services Banner */}
      <section className="px-4 pt-5" data-testid="services-banner-section">
        <button
          onClick={() => navigate('/services')}
          className="w-full bg-minted-800 rounded-sm p-5 text-left text-white relative overflow-hidden group transition-all hover:bg-minted-700"
          data-testid="services-banner"
        >
          <div className="absolute top-0 right-0 w-32 h-32 opacity-20 rounded-full bg-minted-500 -translate-y-1/2 translate-x-1/3" />
          <div className="relative z-10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 border border-minted-500 rounded-sm flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-minted-200" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <p className="text-[10px] tracking-[0.2em] uppercase text-minted-300">New on Minted</p>
                <h3 className="font-display text-base font-medium mt-0.5">Local Services</h3>
                <p className="text-minted-300 text-xs mt-1">Mechanics, hair & beauty, massage, trades & more</p>
              </div>
              <ChevronRight className="w-5 h-5 text-minted-400 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
            </div>
            <div className="flex gap-2 mt-3">
              <span className="text-[10px] bg-white/10 px-2 py-1 rounded-sm tracking-wider uppercase">Free for 1 week</span>
              <span className="text-[10px] bg-white/10 px-2 py-1 rounded-sm tracking-wider uppercase">Then $1/week</span>
            </div>
          </div>
        </button>

        {/* Service Category Previews */}
        <div className="flex gap-3 mt-3 overflow-x-auto pb-1 scrollbar-hide">
          {serviceCategories.map((cat, i) => (
            <button
              key={i}
              onClick={() => navigate('/services')}
              className="flex-shrink-0 w-[72px] text-center group"
              data-testid={`service-cat-${i}`}
            >
              <div className="w-[72px] h-[72px] rounded-sm overflow-hidden bg-stone-200">
                <img src={cat.icon} alt={cat.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <p className="text-[10px] text-stone-500 mt-1.5 leading-tight line-clamp-2">{cat.name}</p>
            </button>
          ))}
        </div>
      </section>

      {/* The Private Collection — 18+ members boutique */}
      <section className="px-4 pt-5" data-testid="private-collection-section">
        <button
          onClick={() => navigate('/private-collection')}
          className="w-full rounded-sm p-5 text-left relative overflow-hidden group transition-all hover:opacity-95"
          style={{ backgroundColor: '#14100e', border: '1px solid rgba(201,169,98,0.35)' }}
          data-testid="private-collection-banner"
        >
          <div className="absolute top-0 right-0 w-28 h-28 rounded-full -translate-y-1/2 translate-x-1/3" style={{ background: 'radial-gradient(circle, rgba(201,169,98,0.22) 0%, transparent 70%)' }} />
          <div className="relative z-10 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ border: '1px solid rgba(201,169,98,0.5)' }}>
              <KeyRound className="w-5 h-5" style={{ color: '#c9a962' }} strokeWidth={1.3} />
            </div>
            <div className="flex-1">
              <p className="text-[10px] tracking-[0.25em] uppercase" style={{ color: 'rgba(201,169,98,0.8)' }}>Members Only · 18+</p>
              <h3 className="text-base mt-0.5" style={{ fontFamily: 'Georgia, serif', color: '#f0e9df' }}>The Private Collection</h3>
              <p className="text-xs mt-1" style={{ color: '#a89a8c' }}>18 or over? There may be something here you'd enjoy.</p>
            </div>
            <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" style={{ color: 'rgba(201,169,98,0.6)' }} strokeWidth={1.5} />
          </div>
        </button>
      </section>

      {/* Recently Viewed */}
      <section className="px-4 pt-5">
        <RecentlyViewed />
      </section>

      {/* Stores Section */}
      {stores.length > 0 && (
        <section className="px-4 py-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Store className="w-4 h-4 text-minted-600" strokeWidth={1.5} />
              <h2 className="font-display text-lg font-medium text-stone-900">Shops</h2>
              <span className="text-[10px] font-semibold bg-[#1a3a2a] text-[#f3e5b0] rounded-full px-2 py-0.5" data-testid="home-stores-count">{totalStores}</span>
            </div>
            <button
              onClick={() => navigate('/stores')}
              className="text-minted-700 text-xs font-medium flex items-center gap-1 hover:text-minted-900 transition-colors tracking-wider uppercase"
              data-testid="home-view-all-stores"
            >
              View All {totalStores}
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
          <div className="grid grid-cols-1 gap-3">
            {stores.slice(0, 2).map((store) => (
              <StoreCard key={store.id} store={store} />
            ))}
          </div>
        </section>
      )}

      {/* Categories */}
      <section className="px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-medium text-stone-900">Categories</h2>
          <button
            onClick={() => navigate('/categories')}
            className="text-minted-700 text-xs font-medium flex items-center gap-1 hover:text-minted-900 transition-colors tracking-wider uppercase"
          >
            View All
            <ChevronRight className="w-3 h-3" />
          </button>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {categories.slice(0, 8).map((category) => (
            <CategoryCard key={category.id} category={category} />
          ))}
        </div>
      </section>

      {/* Shop Local - pickup near the visitor's state */}
      {buyerState && localProducts.length > 0 && (
        <section className="px-4 pb-6" data-testid="shop-local-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-minted-600" strokeWidth={1.5} />
              <h2 className="font-display text-lg font-medium text-stone-900">Pickup Near You</h2>
              <span className="text-[10px] font-semibold bg-[#1a3a2a] text-[#f3e5b0] rounded-full px-2 py-0.5">{buyerState}</span>
            </div>
            <button
              onClick={() => navigate(`/browse?state=${buyerState}&pickup=true`)}
              className="text-minted-700 text-xs font-medium flex items-center gap-1 hover:text-minted-900 transition-colors tracking-wider uppercase"
              data-testid="shop-local-see-all"
            >
              See All
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {localProducts.slice(0, 4).map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </section>
      )}

      {/* Featured Products */}
      <section className="px-4 pb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-medium text-stone-900">Just Listed</h2>
          <button
            onClick={() => navigate('/products')}
            className="text-minted-700 text-xs font-medium flex items-center gap-1 hover:text-minted-900 transition-colors tracking-wider uppercase"
          >
            See All
            <ChevronRight className="w-3 h-3" />
          </button>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2 scrollbar-hide">
          {['all', 'new', 'price-low', 'nearby'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-sm text-xs font-medium whitespace-nowrap transition-colors tracking-wider uppercase ${
                activeTab === tab
                  ? 'bg-minted-800 text-white'
                  : 'bg-white text-stone-500 border border-stone-200 hover:border-minted-300 hover:text-minted-800'
              }`}
            >
              {tab === 'all' && 'All'}
              {tab === 'new' && 'Newest'}
              {tab === 'price-low' && 'Price: Low'}
              {tab === 'nearby' && 'Nearby'}
            </button>
          ))}
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-2 gap-3">
          {featuredProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>

        <Button
          variant="outline"
          className="w-full mt-5 rounded-sm border-minted-300 text-minted-800 hover:bg-minted-800 hover:text-white hover:border-minted-800 transition-all h-11"
          onClick={() => navigate('/products')}
        >
          Browse All Products <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
        </Button>
      </section>

      {/* CTA Banners */}
      {!isAuthenticated ? (
        <section className="px-4 pb-6">
          <div className="bg-white border border-stone-200 rounded-sm p-6 text-center">
            <ShoppingBag className="w-8 h-8 text-minted-600 mx-auto mb-3" strokeWidth={1.5} />
            <h3 className="font-display text-lg font-medium text-stone-900 mb-2">Join Minted</h3>
            <p className="text-stone-500 text-sm mb-5">
              Buy & sell preloved items and local services
            </p>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => navigate('/register')} className="bg-minted-800 hover:bg-minted-700 text-white rounded-sm">
                Sign Up Free
              </Button>
              <Button onClick={() => navigate('/login')} variant="outline" className="rounded-sm border-stone-300 hover:border-minted-600">
                Sign In
              </Button>
            </div>
          </div>
        </section>
      ) : (
        <section className="px-4 pb-6">
          <div className="bg-white border border-stone-200 rounded-sm p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-minted-50 rounded-sm">
                <Heart className="w-5 h-5 text-minted-700" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <h3 className="font-display font-medium text-stone-900 mb-1">Want to sell?</h3>
                <p className="text-stone-500 text-sm mb-3">
                  Switch to seller mode to list your items
                </p>
                <Button 
                  onClick={switchRole}
                  size="sm"
                  className="bg-minted-800 hover:bg-minted-700 rounded-sm"
                >
                  <RefreshCw className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Switch to Seller
                </Button>
              </div>
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
};

export default HomePage;
